import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';

import { findSkinImage } from './skinIndex';

const SORTS = [
  { key: 'date',   label: 'Newest first' },
  { key: 'profit', label: 'Best profit' },
  { key: 'loss',   label: 'Worst profit' },
  { key: 'roi',    label: 'Best ROI' },
];

export default function SoldTab2({ rows, settings, skinsData = [], onEdit }) {
  const [query, setQuery] = useState('');
  const [sort, setSort]   = useState('date');

  const sold = useMemo(() => {
    return (rows || [])
      .map((row, realIndex) => ({ row, realIndex }))
      .filter(({ row }) => row.skin?.value && parseFloat(row.sellPrice) > 0)
      .map(e => {
        const buy    = parseFloat(e.row.buyPrice) || 0;
        const sell   = parseFloat(e.row.sellPrice) || 0;
        const profit = parseFloat(e.row.profit) || 0;
        const roi    = buy > 0 ? profit / buy * 100 : null;
        return { ...e, buy, sell, profit, roi, ts: new Date(e.row.timestamp) };
      });
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q ? sold.filter(e => e.row.skin.value.toLowerCase().includes(q)) : [...sold];
    const by = {
      date:   (a, b) => b.ts - a.ts,
      profit: (a, b) => b.profit - a.profit,
      loss:   (a, b) => a.profit - b.profit,
      roi:    (a, b) => (b.roi ?? -Infinity) - (a.roi ?? -Infinity),
    }[sort];
    return list.sort(by);
  }, [sold, query, sort]);

  // Month groups (only meaningful for date sort; other sorts get one flat group)
  const groups = useMemo(() => {
    if (sort !== 'date') return [{ key: 'ranked', label: 'Ranked results', items: filtered }];
    const map = new Map();
    filtered.forEach(e => {
      const key = `${e.ts.getFullYear()}-${String(e.ts.getMonth()).padStart(2, '0')}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: e.ts.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          items: [],
        });
      }
      map.get(key).items.push(e);
    });
    return [...map.values()];
  }, [filtered, sort]);

  const totals = useMemo(() => {
    const profit  = sold.reduce((s, e) => s + e.profit, 0);
    const revenue = sold.reduce((s, e) => s + e.sell, 0);
    const wins    = sold.filter(e => e.profit > 0).length;
    return { profit, revenue, wins, count: sold.length,
      winRate: sold.length ? Math.round(wins / sold.length * 100) : 0 };
  }, [sold]);

  const profC = settings?.profitTextColor || 'var(--green)';
  const lossC = settings?.lossTextColor   || 'var(--red)';
  const pcol  = (v) => v >= 0 ? profC : lossC;

  if (!sold.length) {
    return (
      <div className="sd2-root">
        <div className="lv2-empty">No sales recorded yet — items you mark as sold will appear here.</div>
      </div>
    );
  }

  return (
    <div className="sd2-root">

      {/* ── Summary ────────────────────────────────── */}
      <div className="sd2-summary">
        <div className="sd2-sum-cell">
          <div className="k">Items sold</div>
          <div className="v">{totals.count}</div>
        </div>
        <div className="sd2-sum-cell">
          <div className="k">Revenue</div>
          <div className="v">${totals.revenue.toFixed(2)}</div>
        </div>
        <div className="sd2-sum-cell">
          <div className="k">Total profit</div>
          <div className="v" style={{ color: pcol(totals.profit) }}>
            {totals.profit >= 0 ? '+' : '−'}${Math.abs(totals.profit).toFixed(2)}
          </div>
        </div>
        <div className="sd2-sum-cell">
          <div className="k">Win rate</div>
          <div className="v">{totals.winRate}%<span style={{ fontSize: '0.91rem', color: 'var(--text-dim)', fontWeight: 600 }}> · {totals.wins}W</span></div>
        </div>
      </div>

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar" style={{ top: 56 }}>
        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Search sold items…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <span style={{ fontSize: '0.92rem', color: 'var(--text-dim)', fontWeight: 600 }}>
          {filtered.length} of {sold.length} sales
        </span>
        <select className="lv2-select" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort sales">
          {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {/* ── Ledger ─────────────────────────────────── */}
      {groups.map(g => {
        const sub = g.items.reduce((s, e) => s + e.profit, 0);
        return (
          <div key={g.key}>
            <div className="sd2-month">
              <h3>{g.label}</h3>
              <span className="line" />
              <span className="sub" style={{ color: pcol(sub) }}>
                {sub >= 0 ? '+' : '−'}${Math.abs(sub).toFixed(2)} · {g.items.length} sale{g.items.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="sd2-list">
              {g.items.map((e, i) => {
                const img = findSkinImage(skinsData, e.row.skin.value, e.row.condition?.value);
                return (
                  <motion.div
                    key={e.realIndex}
                    className="sd2-row"
                    onClick={() => onEdit && onEdit(e.row, e.realIndex)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 14) * 0.03 }}
                  >
                    <div className="sd2-day">
                      <div className="d">{e.ts.getDate()}</div>
                      <div className="w">{e.ts.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    </div>
                    {img
                      ? <img className="sd2-img" src={img} alt="" />
                      : <div className="sd2-img" style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 6 }} />}
                    <div className="sd2-main">
                      <div className="sd2-name" title={e.row.skin.value}>{e.row.skin.value}</div>
                      <div className="sd2-meta">
                        {e.row.condition?.value && <span>{e.row.condition.value}</span>}
                        {e.row.siteFee?.label && <span>{e.row.siteFee.label.split(' ')[0]}</span>}
                        {e.row.receivedFromTradeId && <span className="sd2-badge">From trade</span>}
                      </div>
                    </div>
                    <div className="sd2-flow">
                      <b>${e.buy.toFixed(2)}</b>
                      <span className="arrow">→</span>
                      <b>${e.sell.toFixed(2)}</b>
                    </div>
                    <div className="sd2-profit">
                      <div className="p" style={{ color: pcol(e.profit) }}>
                        {e.profit >= 0 ? '+' : '−'}${Math.abs(e.profit).toFixed(2)}
                      </div>
                      {e.roi != null && (
                        <div className="roi" style={{ color: pcol(e.roi) }}>
                          {e.roi >= 0 ? '+' : '−'}{Math.abs(e.roi).toFixed(1)}% ROI
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
